import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { useCallback } from "react";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import SwivuModal from "../../../react/components/editors/SwivuModal.jsx";
import EditorBase from "../../../bryntum-lib/editors/base/EditorBase.jsx";
import {
  DEEP_LINK_URL_KEYS,
  EDITOR_CONTEXT_MENU_ITEM_KEYS,
  SETTING_TAB_WIDGET_KEYS,
  SETTING_TAB_WIDGET_TABS_DETAILS
} from "../../../other/constants/OtherKeys.jsx";
import { assignViewsState, getEntityShareLink } from "../../../other/utils/otherUtils.jsx";
import { StringHelper } from "@bryntum/schedulerpro";
import { userPreferences } from "../../context/bryntumContext.jsx";
import SettingsBoardPrefTab from "../../../bryntum-lib/editors/settingsEditor/SettingsBoardPrefTab.jsx";
import SettingsImportExportTab from "../../../bryntum-lib/editors/settingsEditor/SettingsImportExportTab.jsx";
import SwivuSettingsIconTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingIconsTab.jsx";
// import SettingsClientAgendaTab from "../../../bryntum-lib/editors/settingsEditor/SettingsClientAgendaTab.jsx";
import SwivuSettingsClientCategoryTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsClientCategoryTab.jsx";
import SwivuSettingsClientTagsTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsClientTagsTab.jsx";
import SwivuSettingsClientStatusTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsClientStatusTab.jsx";
import SettingsIntegrationsTab from "../../../bryntum-lib/editors/settingsEditor/SettingsIntegrationsTab.jsx";
import { SwivuSettingsResourceTypeTab } from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsResourceTypeTab.jsx";
import SwivuSettingsResourceCategoryTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsResourceCategoryTab.jsx";
import SwivuSettingsResourceTagsTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsResourceTagsTab.jsx";
import SwivuSettingsResourceStatusTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsResourceStatusTab.jsx";
import SwivuSettingsTaskGeneralTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsTaskGeneralTab.jsx";
import SwivuSettingsTaskCategoryTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsTaskCategoryTab.jsx";
import SwivuSettingsTaskTagsTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsTaskTagsTab.jsx";
import SwivuSettingsTaskStatusTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsTaskStatusTab.jsx";
import SwivuSettingsContactCategoryTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsContactCategoryTab.jsx";
import SwivuSettingsContactTagsTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsContactTagsTab.jsx";
import SwivuSettingsContactStatusTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsContactStatusTab.jsx";
import SwivuSettingsTeamTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsTeamTab.jsx";
import SwivuSettingsTaskPhaseTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsTaskPhaseTab.jsx";
import SettingsTaskAutoArchiveTab from "../../../bryntum-lib/editors/settingsEditor/SettingsTaskAutoArchiveTab.jsx";
import SwivuSettingsBudgetTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsBudgetTab.jsx";
import SettingsSwivuAssistUpdateTab from "../../../bryntum-lib/editors/settingsEditor/SettingsSwivuAssistUpdateTab.jsx";
import SwivuSettingsIncentivePermissionsTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsIncentivePermissionsTab.jsx";
import SwivuSettingsIncentiveCategoriesAndTagsTab from "../../../bryntum-lib/editors/settingsEditor/SwivuSettingsIncentiveCategoriesAndTagsTab.jsx";


const getListingView = (params = {}) => {
  return {
    readOnly: true,
    type: WIDGET_KEYS.swivuGrid,
    selectionMode: {
      row: true,
      multiSelect: false
    },
    features: {
      filter: true,
      rowReorder: false,
      stripe: { disabled: true },
      cellMenu: false,
    },
    ...params,
  };
};

const getListingDetailView = (params = {}) => {
  return {
    type: "container",
    defaults: {
      hidden: true,
    },
    scrollable: true,
    cls: "flex",
    itemCls: "p-0",
    ...params
  };
};

class SwivuSettingGeneralEditor extends EditorBase {
  static get $name() {
    return "SwivuSettingGeneralEditor";
  }

  static get type() {
    return WIDGET_KEYS.swivuGeneralSettingEditorPopup;
  }

  static get configurable() {
    return {
      title: "General Settings",
      scrollable: true,
      minWidth: "30em",
      maxWidth: "100vw",
      width: "93em",
      minHeight: "600px",
      maxHeight: "auto",
      cls: "h-full",
      closable: true,
      autoClose: false,
      draggable: true,
      stateId: PERSISTENCE_STATE_KEYS.settingGeneral,
      layout: "vbox",
      contentElementCls: "grid grid-cols-2 grid-rows-[auto_1fr]",
      header: {
        cls: "border-b border-border bg-primary/80 text-primary-foreground [&_button]:h-[1.5rem] text-[0.875rem] [&_.b-tool]:text-primary-foreground",
      },
      items: {
        descriptionText: {
          flex: "0 0 auto",
          cls: "col-span-full py-2 px-2 border-l border-l-2 border-l-primary bg-secondary text-muted-foreground rounded-sm",
          html: "General settings impact the user experience of every user of this board only."
        },
        preferenceBoardTabsGrid: getListingView({
          stateId: PERSISTENCE_STATE_KEYS.settingsTabBoardGrid,
          onSelectionChange: "up.onTabGridSelectionChange",
          columns: [
            { id: "tab-board-settings-category", text: "Category", type: WIDGET_KEYS.swivuSortColumn, field: "category" },
            { id: "tab-board-settings-topic", text: "Topic", type: WIDGET_KEYS.swivuSortColumn, field: "topic" }
          ]
        }),
        preferenceBoardDetailView: getListingDetailView({
          items: {
            [SETTING_TAB_WIDGET_KEYS.boardPrefTab]: { type: SettingsBoardPrefTab.type },
            [SETTING_TAB_WIDGET_KEYS.swivuAssistUpdateTab]: { type: SettingsSwivuAssistUpdateTab.type },
            [SETTING_TAB_WIDGET_KEYS.importExportTab]: { type: SettingsImportExportTab.type },
            [SETTING_TAB_WIDGET_KEYS.iconsTab]: { type: SwivuSettingsIconTab.type },
            // [SETTING_TAB_WIDGET_KEYS.clientAgendaTab]: { type: SettingsClientAgendaTab.type },
            [SETTING_TAB_WIDGET_KEYS.clientCategoryTab]: { type: SwivuSettingsClientCategoryTab.type },
            [SETTING_TAB_WIDGET_KEYS.clientTagsTab]: { type: SwivuSettingsClientTagsTab.type },
            [SETTING_TAB_WIDGET_KEYS.clientStatusTab]: { type: SwivuSettingsClientStatusTab.type },
            [SETTING_TAB_WIDGET_KEYS.integrationTab]: { type: SettingsIntegrationsTab.type },
            [SETTING_TAB_WIDGET_KEYS.resourceTypeTab]: { type: SwivuSettingsResourceTypeTab.type },
            [SETTING_TAB_WIDGET_KEYS.resourceCategoryTab]: { type: SwivuSettingsResourceCategoryTab.type },
            [SETTING_TAB_WIDGET_KEYS.resourceTagsTab]: { type: SwivuSettingsResourceTagsTab.type },
            [SETTING_TAB_WIDGET_KEYS.resourceStatusTab]: { type: SwivuSettingsResourceStatusTab.type },
            [SETTING_TAB_WIDGET_KEYS.taskGeneralTab]: { type: SwivuSettingsTaskGeneralTab.type },
            [SETTING_TAB_WIDGET_KEYS.taskCategoryTab]: { type: SwivuSettingsTaskCategoryTab.type },
            [SETTING_TAB_WIDGET_KEYS.taskTagsTab]: { type: SwivuSettingsTaskTagsTab.type },
            [SETTING_TAB_WIDGET_KEYS.taskStatusTab]: { type: SwivuSettingsTaskStatusTab.type },
            [SETTING_TAB_WIDGET_KEYS.taskPhaseTab]: { type: SwivuSettingsTaskPhaseTab.type },
            [SETTING_TAB_WIDGET_KEYS.taskAutoArchiveTab]: { type: SettingsTaskAutoArchiveTab.type },
            [SETTING_TAB_WIDGET_KEYS.contactCategoryTab]: { type: SwivuSettingsContactCategoryTab.type },
            [SETTING_TAB_WIDGET_KEYS.contactTagsTab]: { type: SwivuSettingsContactTagsTab.type },
            [SETTING_TAB_WIDGET_KEYS.contactStatusTab]: { type: SwivuSettingsContactStatusTab.type },
            [SETTING_TAB_WIDGET_KEYS.teamsTab]: { type: SwivuSettingsTeamTab.type },
            [SETTING_TAB_WIDGET_KEYS.budgetTab]: { type: SwivuSettingsBudgetTab.type },
            [SETTING_TAB_WIDGET_KEYS.incentivePermissionsTab]: { type: SwivuSettingsIncentivePermissionsTab.type },
            [SETTING_TAB_WIDGET_KEYS.incentiveCategoriesAndTagsTab]: { type: SwivuSettingsIncentiveCategoriesAndTagsTab.type }
          }
        })
      }
    };
  }

  editRecord(record, context, otherParams) {
    if (!otherParams.isFirstShow) return;

    super.editRecord(record, context, otherParams);
    this.otherParams = otherParams;
    const {
      preferenceBoardTabsGrid,
      preferenceBoardDetailView
    } = this.widgetMap;

    preferenceBoardTabsGrid.store.data = Object.entries(SETTING_TAB_WIDGET_TABS_DETAILS.boardPreferences).map(([prefKey, prefValueObj]) => ({
      tabWidgetRef: prefKey,
      id: prefKey,
      category: prefValueObj.category,
      topic: prefValueObj.topic
    }));

    this.loadBoardPreferences(record, context, otherParams);
    if (!this.isBoardPrefListenerAttached) {
      this.isBoardPrefListenerAttached = userPreferences.boardPreferencesStore.on("change", ({record}) => {
        if (record?.key !== PERSISTENCE_STATE_KEYS.swivuAssistSheetUrl) {
          this.loadBoardPreferences(record, context, otherParams);
        }
      }, this);
    }

    assignViewsState([preferenceBoardTabsGrid], context.stateProvider);

    // editor context menu config
    this.configureEditorContextMenu(record, context, {
      recordStore: context.project.resourceStore,
      items: {
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.shareItem]: {
          enabled: true,
          editorParams() {
            const selectedRecord = preferenceBoardTabsGrid.selectedRecord;
            const tabDeepLinkSlugRec = { id: selectedRecord ? preferenceBoardDetailView.getWidgetById(selectedRecord.id).swivuDeepLinkSlug : "" };
            return {
              message: getEntityShareLink({
                record: tabDeepLinkSlugRec,
                context,
                entityType: DEEP_LINK_URL_KEYS.generalSetting,
                entityDetail: {
                  entityVisibleName: "Board Setting",
                  name: StringHelper.capitalize(tabDeepLinkSlugRec.id)
                }
              })
            };
          }
        }
      }
    });

    preferenceBoardTabsGrid.selectRow({ record: preferenceBoardTabsGrid.selectedRecord || preferenceBoardTabsGrid.store.first });

    // select grid record by share link value
    if (otherParams.selectTabByTypeOnOpen) {

      const boardPrefRecord = preferenceBoardTabsGrid.store.getById(otherParams.selectTabByTypeOnOpen);
      if (boardPrefRecord) {
        setTimeout(() => { // changing tab just before selecting a row breaks into console
          preferenceBoardTabsGrid.selectRow({ record: boardPrefRecord });
        }, 10);
      }
      this.otherParams.selectTabByTypeOnOpen = null;
    }
  }

  onSettingsTabChange() {
    if (!this.otherParams.selectTabByTypeOnOpen) {
      const { preferenceBoardTabsGrid } = this.widgetMap;

      preferenceBoardTabsGrid.selectRow({ record: preferenceBoardTabsGrid.selectedRecord || preferenceBoardTabsGrid.store.first });

    }
  }

  onTabGridSelectionChange({ action, selected, deselected }) {
    if (action === "select") {
      const {preferenceBoardDetailView} = this.widgetMap;
      const [selectedRecord] = selected;
      const [deselectedRecord] = deselected;
      if (selectedRecord) {
        const selectedTab = SETTING_TAB_WIDGET_TABS_DETAILS.boardPreferences?.[selectedRecord.tabWidgetRef];
        if (selectedTab) {
          this.title = `<div class="flex justify-between w-full">
              <div>General Settings</div>
              <div class="mr-6">${selectedTab.category} - ${selectedTab.topic}</div>
              <div></div>
          </div>`;
          preferenceBoardDetailView.getWidgetByRef(selectedRecord.tabWidgetRef).hidden = false;
        }
      }
      if (deselectedRecord) preferenceBoardDetailView.getWidgetByRef(deselectedRecord.tabWidgetRef).hidden = true;
    }
  }

  loadBoardPreferences(record, context, otherParams) {
    const { preferenceBoardDetailView } = this.widgetMap;
    preferenceBoardDetailView.eachWidget((widget) => {
      if (widget.isSettingsTabBase) widget.editRecord(record, context, otherParams);
    });
  }
}

SwivuSettingGeneralEditor.initClass();

const SwivuSettingGeneralPopup = ({ context, record, onClose, isShow, editorInstance }) => {
  const { selectTabByTypeOnOpen } = record;
  const onShowHandler = useCallback((editor, otherParams = {}) => {
    editor.project = context.project;
    editor.editRecord({}, context, Object.assign(otherParams, { selectTabByTypeOnOpen })); // Settings Editor is associated with the whole project; there's no specific "record" to edit.
    editorInstance.editorRef = editor;
  }, [context, editorInstance]);
  return (
    <SwivuModal
      widgetType={WIDGET_KEYS.swivuGeneralSettingEditorPopup}
      setFontSize={context.setFontSize}
      rowHeight={context.rowHeight}
      isShow={isShow}
      onHide={onClose}
      onShow={onShowHandler}
    />
  );
};

export default SwivuSettingGeneralPopup;
